import { useState, useRef } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import Popover from './Popover'

// A bare string is its own value and label; the triple adds a muted second
// line shown under the label in the open list only.
type SelectOption = string | readonly [string, string] | readonly [string, string, string]

function optionParts(opt: SelectOption): { value: string; label: string; description?: string } {
  if (typeof opt === 'string') return { value: opt, label: opt }
  return { value: opt[0], label: opt[1], description: opt.length === 3 ? opt[2] : undefined }
}

interface SelectProps {
  value: string
  onChange: (value: string) => void
  options: readonly SelectOption[]
  placeholder?: string
  className?: string
  disabled?: boolean
  searchable?: boolean
  /** Set when a Field labels this control. */
  id?: string
}

const optionClass = 'w-full flex gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 focus-visible:bg-slate-100 focus:outline-none transition-colors'

export default function Select({ value, onChange, options, placeholder, className = '', disabled, searchable = false, id }: SelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  const selected = options.find(opt => optionParts(opt).value === value)
  const label = selected ? optionParts(selected).label : (placeholder ?? 'Select...')

  const filtered = searchable && query
    ? options.filter(opt => optionParts(opt).label.toLowerCase().includes(query.toLowerCase()))
    : options

  const choose = (val: string) => {
    onChange(val)
    setOpen(false)
    setQuery('')
  }

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { if (!disabled) { setOpen(!open); setQuery('') } }}
        onKeyDown={e => {
          if (!disabled && !open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault()
            setOpen(true)
          }
        }}
        className={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-danone-cyan ${
          disabled
            ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
            : 'border-slate-200 bg-white text-slate-700 cursor-pointer hover:border-slate-300'
        }`}
      >
        <span className="truncate min-w-0" title={label}>{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <Popover open={open && !disabled} anchorRef={ref} onClose={() => { setOpen(false); setQuery('') }}>
        {searchable && (
          <div className="p-2 border-b border-slate-100 flex-shrink-0">
            <input
              autoFocus
              data-popover-search
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search..."
              aria-label="Search options"
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg outline-none focus:border-danone-cyan"
            />
          </div>
        )}
        <div role="listbox" className="py-1 max-h-72 min-h-0 overflow-y-auto">
          {placeholder && !query && (
            <button type="button" role="option" aria-selected={value === ''} data-option onClick={() => choose('')}
              className={`${optionClass} items-center text-slate-400`}>
              {placeholder}
            </button>
          )}
          {filtered.map(opt => {
            const { value: val, label: lbl, description } = optionParts(opt)
            const isSelected = value === val
            return (
              <button key={val} type="button" role="option" aria-selected={isSelected} data-option onClick={() => choose(val)}
                className={`${optionClass} ${description ? 'items-start text-left' : 'items-center text-left'}`}>
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors ${description ? 'mt-0.5 ' : ''}${isSelected ? 'bg-danone-cyan border-danone-cyan' : 'border-slate-300'}`}>
                  {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                </span>
                {description ? (
                  <span className="min-w-0">
                    {lbl}
                    <span className="block text-xs text-slate-500 mt-0.5">{description}</span>
                  </span>
                ) : lbl}
              </button>
            )
          })}
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-400">No results</div>
          )}
        </div>
      </Popover>
    </div>
  )
}
